
import { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Upload, BarChart3, PieChart, TrendingUp, Filter, RefreshCw, FileText } from 'lucide-react';
import { KPICard } from './KPICard';
import { DynamicChart } from './DynamicChart';
import { LearningInsights } from '@/components/ai/LearningInsights';
import { ProactiveInsights } from '@/components/ai/ProactiveInsights';
import { AlertRulesManager } from '@/components/ai/AlertRulesManager';
import { useSelfLearningAnalytics } from '@/hooks/useSelfLearningAnalytics';
import { useProactiveInsights } from '@/hooks/useProactiveInsights';
import { useOpenAIService } from '@/hooks/useOpenAIService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DataColumn {
  name: string;
  type: 'number' | 'string' | 'date' | 'boolean';
  values: any[];
  unique: number;
  nullCount: number;
}

interface ChartConfig {
  id: string;
  type: 'bar' | 'line' | 'pie' | 'area';
  title: string;
  data: any[];
  xAxis?: string;
  yAxis?: string;
  category?: string;
  color?: string;
  size: 'small' | 'medium' | 'large';
}

interface KPIConfig {
  id: string;
  type: 'kpi';
  title: string;
  value: number;
  format: 'number' | 'currency' | 'percentage';
  color?: string;
  trend?: 'up' | 'down' | 'neutral';
  change?: number;
  size: 'small' | 'medium' | 'large';
}

interface FilterConfig {
  column: string;
  operator: 'equals' | 'contains' | 'greater' | 'less' | 'between';
  value: any;
}

export const DynamicDashboard = () => {
  const [rawData, setRawData] = useState<any[]>([]);
  const [columns, setColumns] = useState<DataColumn[]>([]);
  const [industry, setIndustry] = useState<string>('');
  const [charts, setCharts] = useState<ChartConfig[]>([]);
  const [kpis, setKpis] = useState<KPIConfig[]>([]);
  const [filters, setFilters] = useState<FilterConfig[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [availableDatasets, setAvailableDatasets] = useState<any[]>([]);
  const [aiDashboardSuggestions, setAiDashboardSuggestions] = useState<string>('');
  const { callAIService } = useOpenAIService();

  // Filter data based on active filters
  const filteredData = useMemo(() => {
    if (filters.length === 0) return rawData;
    
    return rawData.filter(row => {
      return filters.every(filter => {
        const value = row[filter.column];
        switch (filter.operator) {
          case 'equals':
            return value === filter.value;
          case 'contains':
            return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
          case 'greater':
            return Number(value) > Number(filter.value);
          case 'less':
            return Number(value) < Number(filter.value);
          default:
            return true;
        }
      });
    });
  }, [rawData, filters]);

  // Analyze data structure and types
  const analyzeData = (data: any[]) => {
    if (!data || data.length === 0) return [];

    const sampleSize = Math.min(100, data.length);
    const sample = data.slice(0, sampleSize);
    const columnNames = Object.keys(sample[0] || {});

    return columnNames.map(name => {
      const values = sample.map(row => row[name]).filter(v => v != null);
      const uniqueValues = [...new Set(values)];
      const nullCount = sample.length - values.length;

      // Determine data type
      let type: DataColumn['type'] = 'string';
      if (values.every(v => !isNaN(Number(v)) && isFinite(Number(v)))) {
        type = 'number';
      } else if (values.every(v => !isNaN(Date.parse(String(v))))) {
        type = 'date';
      } else if (values.every(v => typeof v === 'boolean' || v === 'true' || v === 'false')) {
        type = 'boolean';
      }

      return {
        name,
        type,
        values,
        unique: uniqueValues.length,
        nullCount
      };
    });
  };

  const {
    userPreferences,
    trackInteraction,
    getOptimizedChartRecommendations,
    getPrioritizedColumns,
    getSmartFilterSuggestions
  } = useSelfLearningAnalytics();

  const { 
    analyzeDataForInsights, 
    detectAnomalies, 
    generateRecommendations 
  } = useProactiveInsights();

  // Load available datasets on component mount
  useEffect(() => {
    loadAvailableDatasets();
  }, []);

  const loadAvailableDatasets = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('file_storage')
        .select('*')
        .eq('user_id', user.id)
        .or('folder_path.eq./cleaned-data/,folder_path.eq./cleaned-datasets/')
        .eq('is_folder', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAvailableDatasets(data || []);
    } catch (error) {
      console.error('Error loading datasets:', error);
    }
  };

  const loadDatasetForDashboard = async (dataset: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('user_files')
        .download(dataset.file_path);

      if (error) throw error;

      const text = await data.text();
      
      // Parse CSV data
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      const parsedData = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const obj: any = {};
        headers.forEach((header, index) => {
          obj[header] = values[index] || '';
        });
        return obj;
      });

      setRawData(parsedData);
      const analyzedColumns = analyzeData(parsedData);
      setColumns(analyzedColumns);
      
      toast.success(`Loaded dataset: ${dataset.file_name} with ${parsedData.length} records`);
    } catch (error) {
      console.error('Error loading dataset:', error);
      toast.error('Failed to load dataset');
    }
  };

  // Generate AI-powered dashboard suggestions
  const generateAIDashboardSuggestions = async () => {
    if (!columns.length || !filteredData.length) {
      toast.error('Please load data first');
      return;
    }

    try {
      setIsAnalyzing(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please sign in to use AI suggestions');
        return;
      }

      const dataPreview = filteredData.slice(0, 10);
      const columnAnalysis = columns.map(col => ({
        name: col.name,
        type: col.type,
        uniqueValues: col.unique,
        nulls: col.nullCount
      }));

      const suggestions = await callAIService({
        feature: 'dashboard_suggestions',
        data: {
          columns: columnAnalysis,
          sample_data: dataPreview,
          industry: industry || 'general',
          total_rows: filteredData.length
        },
        industry: industry || 'general',
        userId: user.id,
        interactionType: 'dashboard_suggestions'
      });

      setAiDashboardSuggestions(suggestions);
      toast.success('AI dashboard suggestions generated!');
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
      toast.error('Failed to generate AI suggestions');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Enhanced dashboard generation with proactive insights and async processing
  const generateDashboard = async () => {
    if (!columns.length || !filteredData.length) return;

    setIsAnalyzing(true);
    
    // Track dashboard generation interaction
    trackInteraction({
      interactionType: 'dashboard_generate',
      industry,
      dataColumns: columns.map(col => col.name),
      filtersApplied: filters,
      metadata: { columnCount: columns.length, dataSize: filteredData.length }
    });
    
    try {
      // Process dashboard generation in chunks to prevent freezing
      await new Promise(resolve => setTimeout(resolve, 100)); // Allow UI to update
      const newCharts: ChartConfig[] = [];
      const newKpis: KPIConfig[] = [];
      
      // Get AI-powered recommendations based on learning
      const recommendations = getOptimizedChartRecommendations(columns, industry);
      const prioritizedColumns = getPrioritizedColumns(columns);
      
      // Generate KPI cards for numeric columns (prioritized by user preferences)
      const numericColumns = prioritizedColumns.filter(col => col.type === 'number');
      numericColumns.slice(0, 4).forEach((col, index) => {
        const values = filteredData.map(row => Number(row[col.name])).filter(v => !isNaN(v));
        if (values.length === 0) return;

        const sum = values.reduce((a, b) => a + b, 0);
        const avg = sum / values.length;

        let value = sum;
        let format: 'number' | 'currency' | 'percentage' = 'number';
        let title = col.name;

        // Apply learned KPI format preferences
        if (recommendations.kpiFormats.includes('currency') && 
            (col.name.toLowerCase().includes('revenue') || col.name.toLowerCase().includes('price'))) {
          format = 'currency';
          title = `Total ${col.name}`;
        } else if (recommendations.kpiFormats.includes('percentage') && 
                   (col.name.toLowerCase().includes('rate') || col.name.toLowerCase().includes('percentage'))) {
          value = avg;
          format = 'percentage';
          title = `Avg ${col.name}`;
        } else {
          value = avg;
          title = `Avg ${col.name}`;
        }

        newKpis.push({
          id: `kpi-${col.name}`,
          type: 'kpi',
          title,
          value,
          format,
          color: `hsl(${index * 90}, 70%, 50%)`,
          size: 'small'
        });

        // Track KPI interaction
        trackInteraction({
          interactionType: 'kpi_view',
          chartType: 'kpi',
          industry,
          dataColumns: [col.name],
          metadata: { format, value }
        });
      });

      // Generate charts based on learned preferences
      const chartTypePriority = recommendations.chartTypes.length > 0 
        ? recommendations.chartTypes 
        : ['line', 'bar', 'pie', 'area'];

      // Time series charts (prioritized if user prefers line charts)
      const dateColumns = columns.filter(col => col.type === 'date');
      if (dateColumns.length > 0 && numericColumns.length > 0 && chartTypePriority.includes('line')) {
        const dateCol = dateColumns[0];
        const valueCol = numericColumns[0];
        
        const timeSeriesData = filteredData
          .map(row => ({
            date: new Date(row[dateCol.name]).toLocaleDateString(),
            value: Number(row[valueCol.name]) || 0
          }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        newCharts.push({
          id: `trend-${valueCol.name}`,
          type: 'line',
          title: `${valueCol.name} Trend Over Time`,
          data: timeSeriesData,
          xAxis: 'date',
          yAxis: 'value',
          color: 'hsl(210, 70%, 50%)',
          size: 'large'
        });

        trackInteraction({
          interactionType: 'chart_view',
          chartType: 'line',
          industry,
          dataColumns: [dateCol.name, valueCol.name]
        });
      }

      // Categorical charts based on preferences
      const stringColumns = columns.filter(col => 
        col.type === 'string' && col.unique <= 10 && col.unique > 1
      );
      
      stringColumns.slice(0, 2).forEach(col => {
        const categoryData = filteredData.reduce((acc, row) => {
          const category = row[col.name];
          if (!category) return acc;
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const chartData = Object.entries(categoryData).map(([name, value]) => ({
          name,
          value
        }));

        // Choose chart type based on user preferences and data characteristics
        let chartType: 'pie' | 'bar' = 'bar';
        if (chartData.length <= 5 && chartTypePriority.includes('pie')) {
          chartType = 'pie';
        }

        newCharts.push({
          id: `${chartType}-${col.name}`,
          type: chartType,
          title: chartType === 'pie' ? `Distribution by ${col.name}` : `Count by ${col.name}`,
          data: chartData.slice(0, 10),
          xAxis: chartType === 'bar' ? 'name' : undefined,
          yAxis: chartType === 'bar' ? 'value' : undefined,
          size: 'medium'
        });

        trackInteraction({
          interactionType: 'chart_view',
          chartType,
          industry,
          dataColumns: [col.name],
          metadata: { categoryCount: chartData.length }
        });
      });

      // Correlation charts for numeric columns (if user engages with advanced analytics)
      if (numericColumns.length >= 2 && chartTypePriority.includes('area')) {
        const col1 = numericColumns[0];
        const col2 = numericColumns[1];
        
        const scatterData = filteredData.map(row => ({
          x: Number(row[col1.name]) || 0,
          y: Number(row[col2.name]) || 0
        })).filter(d => d.x !== 0 || d.y !== 0);

        if (scatterData.length > 0) {
          newCharts.push({
            id: `correlation-${col1.name}-${col2.name}`,
            type: 'area',
            title: `${col1.name} vs ${col2.name}`,
            data: scatterData,
            xAxis: 'x',
            yAxis: 'y',
            color: 'hsl(280, 70%, 50%)',
            size: 'medium'
          });

          trackInteraction({
            interactionType: 'chart_view',
            chartType: 'area',
            industry,
            dataColumns: [col1.name, col2.name],
            metadata: { correlationType: 'numeric_comparison' }
          });
        }
      }

      // NEW: Trigger proactive insights analysis
      analyzeDataForInsights(filteredData, industry || 'general');
      detectAnomalies(filteredData, industry || 'general');
      if (industry) {
        generateRecommendations(filteredData, industry);
      }

      // Update charts and KPIs in batches to prevent UI blocking
      await new Promise(resolve => {
        setTimeout(() => {
          setCharts(newCharts);
          setKpis(newKpis);
          resolve(void 0);
        }, 100);
      });

      // Generate AI suggestions asynchronously
      if (industry) {
        setTimeout(() => generateAIDashboardSuggestions(), 500);
      }

      toast.success('Smart dashboard generated with AI insights!');
    } catch (error) {
      console.error('Error generating dashboard:', error);
      toast.error('Failed to generate dashboard');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Enhanced filter handling with learning
  const addFilter = (column: string, operator: FilterConfig['operator'], value: any) => {
    const newFilter: FilterConfig = { column, operator, value };
    setFilters(prev => [...prev, newFilter]);
    
    // Track filter interaction
    trackInteraction({
      interactionType: 'filter_apply',
      industry,
      dataColumns: [column],
      filtersApplied: [newFilter],
      metadata: { operator, value }
    });
  };

  // Remove filter
  const removeFilter = (index: number) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  // Get grid layout classes based on chart size
  const getGridClasses = (size: 'small' | 'medium' | 'large') => {
    switch (size) {
      case 'small': return 'col-span-1';
      case 'medium': return 'col-span-2';
      case 'large': return 'col-span-4';
      default: return 'col-span-2';
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        let data: any[] = [];

        if (file.name.endsWith('.json')) {
          data = JSON.parse(content);
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(h => h.trim());
          data = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim());
            const obj: any = {};
            headers.forEach((header, index) => {
              obj[header] = values[index] || '';
            });
            return obj;
          });
        }

        if (data.length > 0) {
          setRawData(data);
          const analyzedColumns = analyzeData(data);
          setColumns(analyzedColumns);
          toast.success(`Loaded ${data.length} records with ${analyzedColumns.length} columns`);
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Failed to parse file');
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Google AI Dashboard Studio</h2>
          <p className="text-muted-foreground">
            Generate intelligent visualizations with Google AI powered analytics and insights
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            onClick={generateAIDashboardSuggestions}
            disabled={!rawData.length || isAnalyzing}
            variant="outline"
            className="flex items-center gap-2"
          >
            <TrendingUp className="h-4 w-4" />
            AI Suggestions
          </Button>
          
          <Button
            onClick={generateDashboard}
            disabled={!rawData.length || isAnalyzing}
            className="flex items-center gap-2"
          >
            {isAnalyzing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <BarChart3 className="h-4 w-4" />
            )}
            Generate Dashboard
          </Button>
        </div>
      </div>

      {/* AI Dashboard Suggestions */}
      {aiDashboardSuggestions && (
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-start gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800 mb-2">Google AI Dashboard Recommendations</h3>
              <div className="text-sm text-blue-700 whitespace-pre-wrap">{aiDashboardSuggestions}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Available Datasets */}
      {availableDatasets.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5" />
            <h3 className="font-medium">Load Cleaned Datasets</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {availableDatasets.map((dataset) => (
              <Button
                key={dataset.id}
                variant="outline"
                size="sm"
                onClick={() => loadDatasetForDashboard(dataset)}
                className="justify-start truncate"
              >
                <FileText className="h-4 w-4 mr-2" />
                {dataset.file_name}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* NEW: Proactive Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProactiveInsights />
        <AlertRulesManager />
      </div>

      {/* Learning Insights Panel */}
      {userPreferences && (
        <LearningInsights />
      )}

      {/* Data Upload */}
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <Upload className="h-5 w-5" />
          <h3 className="font-medium">Data Upload</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <input
              type="file"
              accept=".csv,.json"
              onChange={handleFileUpload}
              className="hidden"
              id="data-upload"
            />
            <label
              htmlFor="data-upload"
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 w-fit"
            >
              <Upload className="h-4 w-4" />
              Upload CSV or JSON file
            </label>
          </div>
          
          {columns.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-sm">
                <span className="font-medium">Records:</span> {rawData.length}
              </div>
              <div className="text-sm">
                <span className="font-medium">Columns:</span> {columns.length}
              </div>
              <div className="text-sm">
                <span className="font-medium">Numeric:</span> {columns.filter(c => c.type === 'number').length}
              </div>
              <div className="text-sm">
                <span className="font-medium">Categorical:</span> {columns.filter(c => c.type === 'string').length}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Filters with Smart Suggestions */}
      {columns.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <Filter className="h-5 w-5" />
            <h3 className="font-medium">Filters</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {filters.map((filter, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => removeFilter(index)}
                >
                  {filter.column} {filter.operator} {filter.value} ×
                </Badge>
              ))}
            </div>
            
            {columns.filter(c => c.type === 'string' && c.unique <= 20).slice(0, 3).map(col => (
              <div key={col.name} className="flex items-center gap-2">
                <span className="text-sm font-medium w-24">{col.name}:</span>
                <Select onValueChange={(value) => addFilter(col.name, 'equals', value)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={`Filter by ${col.name}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {[...new Set(rawData.map(row => row[col.name]))].slice(0, 10).map(value => (
                      <SelectItem key={value} value={value}>{value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Generated Charts */}
      {(charts.length > 0 || kpis.length > 0) && (
        <div className="grid grid-cols-4 gap-6">
          {/* Render KPI Cards */}
          {kpis.map(kpi => (
            <div key={kpi.id} className={getGridClasses(kpi.size)}>
              <KPICard
                title={kpi.title}
                value={kpi.value}
                format={kpi.format}
                color={kpi.color}
                trend={kpi.trend}
                change={kpi.change}
              />
            </div>
          ))}
          
          {/* Render Charts */}
          {charts.map(chart => (
            <div key={chart.id} className={getGridClasses(chart.size)}>
              <DynamicChart
                config={chart}
                data={chart.data}
              />
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!rawData.length && (
        <Card className="p-12 text-center">
          <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No Data Uploaded</h3>
          <p className="text-muted-foreground mb-4">
            Upload a CSV or JSON file to automatically generate a dashboard with intelligent visualizations
          </p>
          <label
            htmlFor="data-upload"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md cursor-pointer hover:bg-primary/90"
          >
            <Upload className="h-4 w-4" />
            Upload Data File
          </label>
        </Card>
      )}
    </div>
  );
};
